import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { LoadManager } from '@theme/custom-collection-utility';
import { TvSubscriptionCartModal } from '@theme/subscription-cart-modal';
import * as Stepper from '@theme/collection-subscription-footer-stepper';

/** @type {NonNullable<typeof window.CollectionSubscriptionFooterUtils>} */
const Utils = window.CollectionSubscriptionFooterUtils ?? (() => {
  throw new Error('CollectionSubscriptionFooterUtils must be loaded before collection-subscription-footer.js');
})();

/**
 * A custom element that manages the collection footer.
 * Keeps only component-level event listeners; helpers live in utils and stepper modules.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} subscriptionFooterContainer
 * @property {HTMLElement} collectionCartButton
 * @property {HTMLElement} nextButton
 * @property {HTMLElement} backButtonDesktop
 * @property {HTMLElement} backButtonMobile
 * @property {HTMLElement} stepProgressTitle
 *
 * @extends {Component<Refs>}
 */
export class CustomCollectionSubscriptionFooter extends Component {
  requiredRefs = ['subscriptionFooterContainer'];

  static STEP_PROGRESS_LABELS = Utils.STEP_PROGRESS_LABELS;
  static ACTIVE_STEP_KEY = Utils.ACTIVE_STEP_KEY;
  static STEP_CHANGED_EVENT = 'tv-subscription-step-changed';
  static ONEOFF_ENABLED_KEY = Utils.ONEOFF_ENABLED_KEY;
  static SYNC_THROTTLE_MS = 150;

  /** @type {string | null} */
  lastFilterStepApplied = null;

  /** @type {MutationObserver | null} */
  #addButtonsObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #addButtonsSyncTimeout = null;

  /** @type {boolean} */
  #stepChangeInProgress = false;

  /** @type {number} */
  #lastSyncAt = 0;

  get stepChangeInProgress() {
    return this.#stepChangeInProgress;
  }
  set stepChangeInProgress(v) {
    this.#stepChangeInProgress = v;
  }

  get lastSyncAt() {
    return this.#lastSyncAt;
  }
  set lastSyncAt(v) {
    this.#lastSyncAt = v;
  }

  get addButtonsSyncTimeout() {
    return this.#addButtonsSyncTimeout;
  }
  set addButtonsSyncTimeout(v) {
    this.#addButtonsSyncTimeout = v;
  }

  get ACTIVE_STEP_KEY() {
    return CustomCollectionSubscriptionFooter.ACTIVE_STEP_KEY;
  }
  get ONEOFF_ENABLED_KEY() {
    return CustomCollectionSubscriptionFooter.ONEOFF_ENABLED_KEY;
  }
  get STEP_PROGRESS_LABELS() {
    return CustomCollectionSubscriptionFooter.STEP_PROGRESS_LABELS;
  }

  getSurveyResultPayload() {
    return Utils.getSurveyResultPayload() || Utils.getRecommendationsPayload();
  }

  getActiveStepHandle() {
    return Utils.getActiveStepHandle();
  }

  /** @param {string} handle */
  setActiveStepHandle(handle) {
    Utils.setActiveStepHandle(handle);
  }

  isOneoffEnabled() {
    return Utils.isOneoffEnabled();
  }

  /** @param {boolean} enabled */
  setOneoffEnabled(enabled) {
    Utils.setOneoffEnabled(enabled);
  }

  /** @param {string|null} v */
  setLastFilterStepApplied(v) {
    this.lastFilterStepApplied = v;
  }

  /** @param {string} handle @param {{ skipFooterSync?: boolean }} [options] */
  dispatchStepChanged(handle, options) {
    try {
      const detail = { handle, ...(options && typeof options === 'object' ? options : {}) };
      window.dispatchEvent(new CustomEvent(CustomCollectionSubscriptionFooter.STEP_CHANGED_EVENT, { detail }));
    } catch {
      // ignore
    }
  }

  async syncAddButtonsForStepQuota() {
    await Stepper.syncAddButtonsForStepQuota(/** @type {any} */ (this));
  }

  /** Bind click and window listeners so they can be re-attached after section morph. */
  #bindEventListeners() {
    if (this.refs.collectionCartButton) {
      this.refs.collectionCartButton.addEventListener('click', this.#handleCollectionCartButton);
    }
    if (this.refs.backButtonDesktop) {
      this.refs.backButtonDesktop.addEventListener('click', this.#handleBackButtonClick);
    }
    if (this.refs.backButtonMobile) {
      this.refs.backButtonMobile.addEventListener('click', this.#handleBackButtonClick);
    }
    if (this.refs.nextButton) {
      this.refs.nextButton.addEventListener('click', this.#handleNextButton);
    }
    window.addEventListener('subscription-survey-completed', this.#handleSurveyCompleted);
    window.addEventListener(CustomCollectionSubscriptionFooter.STEP_CHANGED_EVENT, this.#handleStepChanged);
    window.addEventListener('tv-subscription-cart-modal-opened', this.#handleCartModalOpened);
    window.addEventListener('tv-subscription-cart-inline-opened', this.#handleCartInlineOpened);
    window.addEventListener('tv-subscription-cart-inline-closed', this.#handleCartInlineClosed);
  }

  #unbindEventListeners() {
    if (this.refs.collectionCartButton) {
      this.refs.collectionCartButton.removeEventListener('click', this.#handleCollectionCartButton);
    }
    if (this.refs.backButtonDesktop) {
      this.refs.backButtonDesktop.removeEventListener('click', this.#handleBackButtonClick);
    }
    if (this.refs.backButtonMobile) {
      this.refs.backButtonMobile.removeEventListener('click', this.#handleBackButtonClick);
    }
    if (this.refs.nextButton) {
      this.refs.nextButton.removeEventListener('click', this.#handleNextButton);
    }
    window.removeEventListener('subscription-survey-completed', this.#handleSurveyCompleted);
    window.removeEventListener(CustomCollectionSubscriptionFooter.STEP_CHANGED_EVENT, this.#handleStepChanged);
    window.removeEventListener('tv-subscription-cart-modal-opened', this.#handleCartModalOpened);
    window.removeEventListener('tv-subscription-cart-inline-opened', this.#handleCartInlineOpened);
    window.removeEventListener('tv-subscription-cart-inline-closed', this.#handleCartInlineClosed);
  }

  #reapplyStateAfterSectionRender() {
    if (!this.isConnected) return;
    this.#unbindEventListeners();
    this.#bindEventListeners();
    Stepper.syncStepperFromState(/** @type {any} */ (this));
    void Stepper.maybeApplyFiltersForActiveStep(/** @type {any} */ (this));
    queueMicrotask(() => this.#syncMinOrderHintFromModal());
  }

  /**
   * Footer min-order markup is Liquid from first paint; the subscription cart modal section morphs on
   * cart changes. Mirror the modal's `data-tv-checkout-next-blocked` so the hint does not stay wrong.
   */
  #syncMinOrderHintFromModal() {
    if (!this.isConnected) return;
    const hint = this.querySelector('[data-tv-subscription-footer-min-order]');
    if (!(hint instanceof HTMLElement)) return;

    const modal = document.getElementById('tv-subscription-cart-modal');
    const blockedEl = modal?.querySelector('[data-tv-checkout-next-blocked]');
    if (!(blockedEl instanceof HTMLElement)) return;

    const blocked = blockedEl.getAttribute('data-tv-checkout-next-blocked') === 'true';
    hint.toggleAttribute('hidden', !blocked);
  }

  #scheduleAddButtonsSync() {
    if (this.#stepChangeInProgress) return;
    if (this.#addButtonsSyncTimeout) clearTimeout(this.#addButtonsSyncTimeout);
    this.#addButtonsSyncTimeout = window.setTimeout(() => {
      this.#addButtonsSyncTimeout = null;
      if (this.#stepChangeInProgress) return;
      void this.syncAddButtonsForStepQuota();
    }, 80);
  }

  #observeProductGrid() {
    const resultsList = document.querySelector('results-list[section-id]');
    const sectionId = resultsList?.getAttribute('section-id')?.trim();
    const scope = sectionId
      ? document.getElementById(`shopify-section-${sectionId}`)
      : document.getElementById('MainContent') || document.body;

    if (!scope || this.#addButtonsObserver) return;

    this.#addButtonsObserver = new MutationObserver((mutations) => {
      const fromOurFooter = mutations.some((m) => this.contains(m.target));
      if (fromOurFooter) return;
      const hasChildListChange = mutations.some(
        (m) => m.type === 'childList' && (m.addedNodes?.length || m.removedNodes?.length)
      );
      if (hasChildListChange) this.#scheduleAddButtonsSync();
    });

    this.#addButtonsObserver.observe(scope, { childList: true, subtree: true });
  }

  // --- Event handlers ---

  #handleCartModalOpened = () => {
    void this.syncAddButtonsForStepQuota();
    queueMicrotask(() => this.#syncMinOrderHintFromModal());
  };

  /** When the cart opens as an inline step, ensure the back button is visible. */
  #handleCartInlineOpened = () => {
    Stepper.getBackButtons(/** @type {any} */ (this)).forEach((btn) => btn.classList.remove('no-disp'));
    queueMicrotask(() => this.#syncMinOrderHintFromModal());
  };

  /** When the inline cart step closes, re-sync the stepper to restore the last real step. */
  #handleCartInlineClosed = async () => {
    try {
      await Stepper.syncStepperFromState(/** @type {any} */ (this));
      queueMicrotask(() => this.#syncMinOrderHintFromModal());
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in handleCartInlineClosed', error);
    }
  };

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    queueMicrotask(() => this.#syncMinOrderHintFromModal());

    const sections = /** @type {any} */ (event)?.detail?.data?.sections;
    const sectionId = String(this.getAttribute('data-section-id') || '').trim();

    // MutationObserver handles sync when product grid morphs; #reapplyStateAfterSectionRender
    // handles when footer section is morphed (syncStepperFromState calls syncAddButtonsForStepQuota)
    if (!sections || typeof sections !== 'object') return;
    if (!sectionId || !Object.prototype.hasOwnProperty.call(sections, sectionId)) return;
    window.setTimeout(() => this.#reapplyStateAfterSectionRender(), 0);
  };

  #handleSurveyCompleted = () => {
    this.setOneoffEnabled(false);
    this.setActiveStepHandle('p1');
    this.dispatchStepChanged('p1');
  };

  /** @param {Event} event */
  #handleStepChanged = async (event) => {
    const detail = /** @type {any} */ (event)?.detail || {};
    const handle = String(detail?.handle || '').trim();
    if (!handle) return;
    if (detail?.skipFooterSync) return;

    try {
      this.#stepChangeInProgress = true;
      if (this.#addButtonsSyncTimeout) {
        clearTimeout(this.#addButtonsSyncTimeout);
        this.#addButtonsSyncTimeout = null;
      }
      LoadManager.addLoader();
      const fromHandle = this.getActiveStepHandle();
      this.setActiveStepHandle(handle);
      // Sync filters first so the product grid reloads with new buttons before
      // syncStepperFromState runs. This matches the order in goToPreviousStep and
      // ensures that freshly rendered add buttons in the updated grid are correctly
      // disabled when the new step's quota is already full (e.g. change-meals).
      await Utils.syncFiltersForStepChange(fromHandle, handle, {
        setLastFilterStepApplied: (v) => this.setLastFilterStepApplied(v),
      }, Utils.STEP_CHANGE_OPTIONS);
      await Stepper.syncStepperFromState(/** @type {any} */ (this));
      await Stepper.syncCartAndProductCardsForStepChange();
    } finally {
      this.#stepChangeInProgress = false;
      LoadManager.removeLoader();
      window.setTimeout(() => Utils.scrollToTopOnStepChange(), 200);
    }
  };

  #handleCollectionCartButton = async () => {
    try {
      if (TvSubscriptionCartModal.open()) return;
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in handleCollectionCartButton', error);
      throw error;
    }
  };

  /** @param {Event} event */
  #handleNextButton = async (event) => {
    try {
      event.preventDefault();
      await Stepper.advanceToNextStep(/** @type {any} */ (this), { source: 'footer' });
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in handleNextButton', error);
      throw error;
    }
  };

  /** @param {Event} event */
  #handleBackButtonClick = async (event) => {
    try {
      event.preventDefault();
      // When the cart is showing as an inline step, the back button closes it
      // and returns to the product grid — no need to change the active step handle.
      if (TvSubscriptionCartModal.isInlineStep()) {
        TvSubscriptionCartModal.closeInline();
        return;
      }
      await Stepper.goToPreviousStep(/** @type {any} */ (this));
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in handleBackButtonClick', error);
      throw error;
    }
  };

  #handleCartClose = async () => {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'close' in cartDrawer && typeof /** @type {any} */ (cartDrawer).close === 'function') {
        /** @type {any} */ (cartDrawer).close();
      }
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in handleCartClose', error);
      throw error;
    }
  };

  /** @param {Event} event */
  handleCartNextButton = async (event) => {
    await this.#handleNextButton(event);
    await this.#handleCartClose();
  };

  // --- Lifecycle ---

  connectedCallback() {
    try {
      super.connectedCallback();

      this.#bindEventListeners();
      window.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

      void (async () => {
        try {
          await TvSubscriptionCartModal.reconcileProfileKeys();
        } catch {
          // ignore — no thresholds / modal not in DOM
        }
        await Stepper.correctActiveStepToFirstUnfilled(/** @type {any} */ (this));
        await Stepper.syncStepperFromState(/** @type {any} */ (this));
        void Stepper.maybeApplyFiltersForActiveStep(/** @type {any} */ (this));

        this.#observeProductGrid();
        // syncStepperFromState above already calls syncAddButtonsForStepQuota
        queueMicrotask(() => this.#syncMinOrderHintFromModal());
      })();
    } catch (error) {
      console.error('[COLLECTION-SUBSCRIPTION-FOOTER] Error in connectedCallback', error);
      throw error;
    }
  }

  disconnectedCallback() {
    window.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#unbindEventListeners();
    if (this.#addButtonsObserver) {
      this.#addButtonsObserver.disconnect();
      this.#addButtonsObserver = null;
    }
    if (this.#addButtonsSyncTimeout) {
      clearTimeout(this.#addButtonsSyncTimeout);
      this.#addButtonsSyncTimeout = null;
    }
    super.disconnectedCallback();
  }

  // --- Public API (used by cart modal) ---

  async goToPreviousStep() {
    return Stepper.goToPreviousStep(/** @type {any} */ (this));
  }

  /** @param {{ source?: 'footer'|'cart-modal' }} [options] */
  async advanceToNextStep(options) {
    return Stepper.advanceToNextStep(/** @type {any} */ (this), options);
  }
}

if (!customElements.get('custom-collection-subscription-footer')) {
  customElements.define('custom-collection-subscription-footer', CustomCollectionSubscriptionFooter);
}
